import React from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Card,
  CardHeader,
  Modal,
  IconButton,
  Icon,
  Paper,
  InputBase,
  Box,
  Typography,
  Divider,
  Button,
  Tooltip
} from '@material-ui/core';

import clsx from 'clsx';
import SearchIcon from '@material-ui/icons/Search';
import queryString from 'query-string';
import NothingFound from '../../../shared/components/NothingFound/NothingFound';

// @local
import LoaderContent from '../../../design-system/components/Loader/LoaderContent';
import Toast from '../../../design-system/components/Toast/Toast';
import Table from '../../../design-system/components/Table/Table';
import { requestsLog } from '../ColumnsConfig';
import { ThinkificService } from '@sdk-point/talisis';
import { useStyles } from '../../../assets/styles/jss/pages/microcredentials/useStyles';

const ThinkificServiceSDK = new ThinkificService(
  process.env.REACT_APP_ENV_V2,
  process.env.REACT_APP_X_API_KEY_V2
);

const MgmtCredentialsTablePage = () => {
  const classes = useStyles();
  const location = useLocation();

  const [toast, setToast] = React.useState({ toastProps: { message: '', open: false } });
  const [columns, setColumns] = React.useState([]);
  const [state, setState] = React.useState({ data: [], dataSource: [] });
  const [loading, setLoading] = React.useState(false);
  const [openModalDetails, setOpenModalDetails] = React.useState({ payload: {}, show: false });
  const [inputSearch, setInputSearch] = React.useState('');

  const [progress, setProgress] = React.useState({});
  const progressIntervalsRef = React.useRef({});

  const messageTable = {
    caption: 'No se encontraron resultados.',
    subtitle: 'Intenta con otra nombre'
  };

  // ---------------------- Inicialización ----------------------
  React.useEffect(() => {
    queryString.parse(location.search);
    setColumns([...requestsLog, { name: "accion", minWidth: 50, align: 'center' }]);
    getAllRequests();
  }, [location.search]);

  // ---------------------- Obtener datos ----------------------
  const getAllRequests = async () => {
    try {
      setLoading(true);
      const response = await ThinkificServiceSDK.getCredentials();
      const data = response?.data || [];
      setState({ data, dataSource: data });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setToast({
        toastProps: {
          severity: "error",
          open: true,
          message: err?.message || "Ops! Ha ocurrido un error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Barra de progreso animada ----------------------
  const startProgress = (id, onComplete) => {

    if (progressIntervalsRef.current[id]) {
      clearInterval(progressIntervalsRef.current[id]);
      delete progressIntervalsRef.current[id];
    }

    let current = 0;

    setProgress(prev => ({ ...prev, [id]: 0 }));

    const interval = setInterval(() => {
      current += Math.floor(Math.random() * 4) + 2;

      if (current >= 100) {
        current = 100;
        clearInterval(interval);
        delete progressIntervalsRef.current[id];
        setProgress(prev => {
          const newProgress = { ...prev, [id]: current };
          return newProgress;
        });

        if (onComplete) {
          setTimeout(() => {
            onComplete();
          }, 500);
        }
      } else {
        setProgress(prev => ({ ...prev, [id]: current }));
      }
    }, 1200);

    progressIntervalsRef.current[id] = interval;
  };

  // ---------------------- Acción: volver a enviar ----------------------
  const handleFailedClick = async (row) => {

    if (!row || !row.user_id) {
      console.error('Error: row o user_id no está definido');
      return;
    }

    const id = row.user_id;

    try {

      startProgress(id, () => {

        window.location.reload();
      });

      setToast({
        toastProps: {
          severity: "info",
          open: true,
          message: `Procesando registro ID: ${row.user_id}`,
        },
      });

      const { bundle_content } = row;
      await ThinkificServiceSDK.postDetectMessage(bundle_content);



    } catch (error) {

      if (progressIntervalsRef.current[id]) {
        clearInterval(progressIntervalsRef.current[id]);
        delete progressIntervalsRef.current[id];
      }

      setProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[id];
        return newProgress;
      });

      setToast({
        toastProps: {
          severity: "error",
          open: true,
          message: error?.message || "Error al ejecutar la acción",
        },
      });
    }
  };

  // ---------------------- Filtro por nombre ----------------------
  const onChangeSearch = (e) => {
    const value = e.target.value;
    setInputSearch(value);

    if (!value.trim()) {
      setState(prev => ({ ...prev, data: prev.dataSource }));
      return;
    }

    const filtered = state.dataSource.filter((item) => {
      const name = `${item?.first_name || ''} ${item?.last_name || ''}`;
      return name.toLowerCase().includes(value.toLowerCase());
    });

    setState(prev => ({ ...prev, data: filtered }));
  };

  const getRowId = (row, idx) => row?.id || row?.user_id || row?.userId || idx;

  // ---------------------- Render ----------------------
  return (
    <Box>
      <Typography variant="h1" className={classes.title}>
        Listado de Credenciales
      </Typography>

      <Box className={classes.container}>
        {/* Buscador */}
        <Box mb={3}>
          <Paper component="form" className={classes.searchBox}>
            <IconButton className={classes.iconButtonSearch} aria-label="search">
              <SearchIcon />
            </IconButton>
            <InputBase
              onChange={onChangeSearch}
              className={classes.inputBase}
              placeholder="Buscar por nombre"
              name="searchUser"
              value={inputSearch}
            />
          </Paper>
        </Box>

        {/* Tabla */}
        <LoaderContent loading={loading} minHeight={500}>
          {state.data.length > 0 ? (
            <Card className={classes.catalogsTable}>
              <Table
                maxHeight="800px"
                columns={columns}
                rows={state.data.map((row, idx) => {
                  const rowId = getRowId(row, idx);
                  const baseRow = columns.reduce((acc, col) => {
                    if (col.name === "accion") return acc;

                    if (col.name === "pok_status") {
                      const isSent = row[col.name] === "processing";
                      const status = isSent ? "Enviado" : "Error de envío";
                      const showCourseTooltip =
                        row.credential_type == null &&
                        row.request_type === "insignia" &&
                        row.bundle_content;
                      const bundleContent =
                        typeof row.bundle_content === "string"
                          ? (() => {
                              try {
                                return JSON.parse(row.bundle_content);
                              } catch {
                                return null;
                              }
                            })()
                          : row.bundle_content;
                      const courseName = bundleContent?.payload?.course?.name ?? "-";

                      const statusContent = (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              minWidth: "120px",
                            }}
                          >
                            <Icon
                              className={isSent ? "ri-checkbox-circle-fill" : "ri-error-warning-line"}
                              style={{
                                color: isSent ? "#4caf50" : "#f44336",
                                fontSize: 22,
                              }}
                            />
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 500
                              }}
                            >{status}</span>
                          </div>
                        </div>
                      );

                      acc[col.name] = showCourseTooltip ? (
                        <Tooltip
                          title={
                            <Typography variant="body2" component="span" style={{ display: 'block' }}>
                              Le falta la regla por configurar. Curso: {courseName}
                            </Typography>
                          }
                          placement="top"
                          arrow
                          classes={{
                            tooltip: classes.tooltipRuleMessage,
                            arrow: classes.tooltipRuleArrow,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                            {statusContent}
                          </div>
                        </Tooltip>
                      ) : (
                        statusContent
                      );
                    } else {
                      acc[col.name] = row[col.name] ?? "-";
                    }

                    return acc;
                  }, { id: rowId });

                  // ---------------------- Render acción con barra ----------------------
                  baseRow.accion = (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "140px" }}>
                     
                      {row.pok_response_error_flag !== "N" && progress[row.user_id] !== undefined && progress[row.user_id] > 0 && progress[row.user_id] < 100 ? (
                        <div
                          style={{
                            width: "100%",
                            height: "10px",
                            borderRadius: "6px",
                            background: "#3a3a3a",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${progress[row.user_id]}%`,
                              height: "100%",
                              background: "#7c3aed",
                              transition: "width 0.4s ease",
                            }}
                          />
                        </div>
                      ) : row.pok_response_error_flag !== "N" ? (
                        <button
                          style={{
                            padding: "6px 14px",
                            backgroundColor: "#7c3aed",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                          onClick={() => handleFailedClick(row)}
                        >
                          Volver a enviar
                        </button>
                      ) : (
                        <>
                          {row.request_type === "insignia" && (
                            <Icon className="ri-award-fill" style={{ color: "#ffffff", fontSize: 24 }} />
                          )}
                          {row.request_type === "microcredencial" && (
                            <Icon className="ri-article-line" style={{ color: "#ffffff", fontSize: 24 }} />
                          )}
                        </>
                      )}
                    </div>
                  );

                  return baseRow;
                })}
              />
            </Card>
          ) : (
            !loading && (
              <NothingFound
                width='200px'
                caption={messageTable.caption}
                subcaption={messageTable.subtitle}
              />
            )
          )}
        </LoaderContent>
      </Box>

      <Modal open={openModalDetails.show}>
        <Card>
          <CardHeader
            action={
              <IconButton aria-label="go to back">
                <Icon className={clsx('text-white', 'ri-close-line', 'font-size-24')} />
              </IconButton>
            }
          />
        </Card>
      </Modal>

      <Toast
        {...toast.toastProps}
        duration={5000}
        onFinished={() =>
          setToast({ toastProps: { ...toast.toastProps, open: false } })
        }
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({ ...state.userReducer });
export default connect(mapStateToProps, {})(MgmtCredentialsTablePage);
